import { useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../client'
import { chaves } from './chamados'
import type { ApuracaoNaTela } from '../../types/chamado'

/**
 * Os atos que alimentam a apuração com dado que NÃO vem do e-Niterói.
 *
 * Ficam à parte de `chamados.ts` por uma razão de tamanho e uma de sentido. A de tamanho é o teto de
 * 200 linhas. A de sentido é melhor: tudo em `chamados.ts` se apura sobre chamados que o sistema já
 * conhece — a listagem, o fechamento, a marcação do fiscal. Estes três não: o valor faturado sai da
 * nota fiscal e os quatro números de infraestrutura saem das ferramentas da contratada. Alguém
 * precisa lê-los e registrá-los, e é isso que estes hooks fazem.
 */

/**
 * Registra o valor faturado da competência — a base sobre a qual o desconto incide.
 *
 * `ordemDeServicoId` é opcional e é PROCEDÊNCIA, não base de cálculo: apontar a ordem não muda o
 * desconto, que continua incidindo sobre um valor único do mês com um teto único (decisão do dono,
 * 26/08/2026). As competências anteriores ao registro de ordens continuam sendo lançadas sem ele.
 *
 * @returns a mutação do registro
 */
export function useRegistrarValorDaFatura() {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: async ({
      competencia,
      valor,
      fundamento,
      ordemDeServicoId,
    }: {
      competencia: string
      valor: number
      fundamento: string
      ordemDeServicoId?: number | null
    }) =>
      (
        await api.post<ApuracaoNaTela>(`/fiscalizacao/${competencia}/valor-da-fatura`, {
          valor,
          fundamento,
          ordemDeServicoId: ordemDeServicoId ?? null,
        })
      ).data,
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: chaves.tudo() })
    },
  })
}

/**
 * Retira o valor faturado vigente, sem apagá-lo.
 *
 * @returns a mutação da retirada
 */
export function useRevogarValorDaFatura() {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: async ({ competencia, motivo }: { competencia: string; motivo: string }) =>
      (await api.post<ApuracaoNaTela>(`/fiscalizacao/${competencia}/valor-da-fatura/revogar`, { motivo })).data,
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: chaves.tudo() })
    },
  })
}

/**
 * Registra a medição de infraestrutura do mês — os quatro sub-indicadores do IIE.
 *
 * @returns a mutação do registro
 */
export function useRegistrarInfraestrutura() {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: async ({ competencia, ...corpo }: { competencia: string } & Record<string, unknown>) =>
      (await api.post<ApuracaoNaTela>(`/fiscalizacao/${competencia}/infraestrutura`, corpo)).data,
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: chaves.tudo() })
    },
  })
}

/**
 * Retira a medição de infraestrutura vigente, sem apagá-la.
 *
 * **O endpoint existia desde sempre e não tinha chamador nenhum**, e a falta fechava um beco: a
 * segunda medição do mês é recusada com uma mensagem que manda *"retire a atual com o motivo e
 * registre a correta"*, `trg_iie_medicao_imutavel` barra UPDATE e DELETE no banco, e não havia
 * botão. Quem errasse um dos nove números perdia a competência. É o mesmo par que o valor da fatura
 * já tinha: registrar e retirar, os dois pela tela.
 *
 * @returns a mutação da retirada
 */
export function useRevogarInfraestrutura() {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: async ({ competencia, motivo }: { competencia: string; motivo: string }) =>
      (await api.post<ApuracaoNaTela>(`/fiscalizacao/${competencia}/infraestrutura/revogar`, { motivo })).data,
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: chaves.tudo() })
    },
  })
}
